import { useEffect, useState } from 'react'
import { useVideoPlayer, VideoPlayer } from 'expo-video'
import { useEvent } from 'expo'
import { useIsFocusedRef } from '@ui/hooks/useIsFocused'
import { useNavigation } from 'expo-router'

const VIDEO_END_OFFSET = 1.8 // seconds

const getOverlayVisible = (
  isPlaying: boolean,
  currentTime: number,
  duration: number,
  overlayTime?: number | null
) => {
  if (!isPlaying) return true
  if (overlayTime) return currentTime >= overlayTime
  if (!duration) return false
  return currentTime >= duration - VIDEO_END_OFFSET
}

// Helper to bypass react-hooks/immutability for intentional player mutations
const seekTo = (player: VideoPlayer, seconds: number) => {
  // eslint-disable-next-line react-hooks/immutability
  player.currentTime = seconds
}

const useVideoControls = ({
  autoplay,
  overlayTime,
}: {
  autoplay?: boolean
  overlayTime?: number | null
}) => {
  // useVideoPlayer returns a stable instance — same reference across renders
  const player = useVideoPlayer(null)
  const isFocused = useIsFocusedRef()
  const navigation = useNavigation()

  const { isPlaying } = useEvent(player, 'playingChange', {
    isPlaying: player.playing,
  })
  const { status } = useEvent(player, 'statusChange', {
    status: player.status,
  })

  const [isOverlayVisible, setIsOverlayVisible] = useState(true)

  const play = () => {
    if (!isFocused.current) return
    player.play()
  }

  const pause = () => {
    player.pause()
  }

  const replay = () => {
    player.replay()
  }

  // Pause near end and update overlay visibility on time updates
  useEffect(() => {
    const sub = player.addListener('timeUpdate', ({ currentTime }) => {
      const { duration } = player
      if (
        player.playing &&
        duration &&
        currentTime >= duration - VIDEO_END_OFFSET
      ) {
        player.pause()
      }
      setIsOverlayVisible(
        getOverlayVisible(player.playing, currentTime, duration, overlayTime)
      )
    })
    return () => sub.remove()
  }, [player, overlayTime])

  // Autoplay or seek to near-end position when video is ready
  useEffect(() => {
    if (status !== 'readyToPlay') return
    if (autoplay && isFocused.current) {
      player.play()
      return
    }
    seekTo(player, player.duration ? player.duration - VIDEO_END_OFFSET : 0)
  }, [status, autoplay, player, isFocused])

  // Pause on navigation blur
  useEffect(() => {
    const unsubscribe = navigation.addListener('blur', () => player.pause())
    return unsubscribe
  }, [navigation, player])

  // Pause when screen loses focus
  useEffect(() => {
    if (!isFocused.current && player.playing) {
      player.pause()
    }
  }, [isPlaying, player, isFocused])

  const togglePlay = () => {
    const overlayTimeSeconds = player.duration
      ? player.duration - VIDEO_END_OFFSET
      : 0
    if (player.playing) {
      player.pause()
      seekTo(player, overlayTimeSeconds)
    } else {
      seekTo(player, 0)
      player.play()
    }
  }

  return {
    player,
    play,
    pause,
    replay,
    isPlaying,
    isOverlayVisible,
    togglePlay,
  }
}

export default useVideoControls
